// ── BCHNostr — Sponsored Business Ads ─────────────────────────────
//
// A VERIFIED business Page pays BCH to appear as a clearly-labeled
// "Sponsored" card woven into the main community feed.
//
// Reuses, never rebuilds:
//   · the platform fee address as the payment sink (lib/platform.ts),
//   · the existing on-chain audit to CONFIRM payment (verifyBoostPayment,
//     which itself wraps lib/paymentAudit.ts),
//   · the kind 30078 app-data convention every other boost rides on.
//
// TRUST MODEL (enforced in useBusinessAds, not here):
//   1. The ad event is authored + signed BY THE BUSINESS PAGE itself, so
//      `data.businessPubkey` MUST equal `ev.pubkey`. A `d`/`t` tag is not a
//      trust boundary — only the signature is.
//   2. The author must have a live kind 30499 business profile.
//   3. The author must be CURRENTLY VERIFIED via an issuer-signed grant
//      (lib/businessVerify.ts). Self-asserted markers do NOT qualify.
//   4. The payment txid must be confirmed on-chain to the platform wallet.
//
// A card that fails ANY of these is never rendered. Ads are ranked by amount
// paid (highest first) and capped, exactly like post boosts.

import { PLATFORM_FEE_ADDRESS } from './platform';
export { verifyBoostPayment, type BoostVerifyResult } from './boost';

// ── Event shape ───────────────────────────────────────────────────

/** All sponsored-ad side-data rides on the shared NIP-78 app-data kind. */
export const BUSINESS_AD_KIND = 30078;

/** One active ad per business — addressable by the business's own pubkey. */
export const BUSINESS_AD_D_TAG_PREFIX = 'bchnostr/business-ad/'; // + businessPubkey

/** Indexable discovery tag so the feed can query all ads server-side. */
export const BUSINESS_AD_TAG = 'bch-business-ad';

/** Payment for an ad goes to the platform wallet (same as boosts). */
export const BUSINESS_AD_WALLET = PLATFORM_FEE_ADDRESS;

/** d-tag for a given business's ad event. */
export function businessAdDTag(businessPubkey: string): string {
  return BUSINESS_AD_D_TAG_PREFIX + businessPubkey.trim().toLowerCase();
}

// ── Pricing (flat duration tiers) ─────────────────────────────────
// Priced in USD, converted to sats at purchase time against the live BCH
// price — never a hard-coded sat amount, so the fee tracks the dollar value.
// Deliberately cheap to seed adoption in an early community; raise later.

export interface BusinessAdTier {
  id: '1d' | '7d' | '30d';
  label: string;
  /** Duration in seconds. */
  durationSecs: number;
  /** Whole days, for display + the stored payload. */
  durationDays: number;
  priceUsd: number;
  emoji: string;
}

export const BUSINESS_AD_TIERS: BusinessAdTier[] = [
  { id: '1d',  label: '1 Day',   durationSecs: 1 * 86400,  durationDays: 1,  priceUsd: 1,  emoji: '\u26A1' },      // ⚡ $1
  { id: '7d',  label: '1 Week',  durationSecs: 7 * 86400,  durationDays: 7,  priceUsd: 5,  emoji: '\u{1F525}' },   // 🔥 $5
  { id: '30d', label: '1 Month', durationSecs: 30 * 86400, durationDays: 30, priceUsd: 15, emoji: '\u{1F680}' },   // 🚀 $15
];

/** Sanity cap: no ad may last longer than this from its creation. */
const MAX_AD_DURATION = 31 * 86400;

/** Look up a tier by id (or null). */
export function getBusinessAdTier(id: string): BusinessAdTier | null {
  return BUSINESS_AD_TIERS.find(t => t.id === id) || null;
}

/** Max short pitch length; keeps cards tidy + limits abuse surface. */
export const BUSINESS_AD_HEADLINE_MAX = 90;

// ── Payload ───────────────────────────────────────────────────────

export interface BusinessAdData {
  /** The Business Page being promoted. MUST equal the event author. */
  businessPubkey: string;
  /** Amount actually paid, in sats. */
  priceSats: number;
  /** USD value at purchase (display only). */
  bidUsd: number;
  /** Whole days the ad runs. */
  durationDays: number;
  /** Unix seconds when the ad stops showing. */
  expiresAt: number;
  /** On-chain payment proof. */
  txid: string;
  /** Optional short pitch (sanitized, plain text). */
  headline?: string;
}

// ── Sanitizers ────────────────────────────────────────────────────

const TXID_RE = /^[0-9a-f]{64}$/i;
const HEX64_RE = /^[0-9a-f]{64}$/i;

/**
 * Clamp expiresAt: fix ms→s confusion, reject absurd/past values, and cap to
 * MAX_AD_DURATION from the event's creation. Mirrors lib/boost.ts.
 */
function sanitizeExpiresAt(expiresAt: unknown, createdAt?: number): number {
  let v = typeof expiresAt === 'number' && isFinite(expiresAt) ? expiresAt : 0;
  if (v <= 0) return 0;
  if (v > 10_000_000_000) v = Math.floor(v / 1000); // ms → s
  const now = Math.floor(Date.now() / 1000);
  const reference = createdAt && createdAt > 0 ? createdAt : now;
  const maxExpiry = reference + MAX_AD_DURATION;
  if (v > maxExpiry) v = maxExpiry;
  return v;
}

/** Strip markup/control chars from a headline; never trust event text. */
export function sanitizeHeadline(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/[\u0000-\u001f\u007f]/g, ' ') // control chars
    .replace(/[<>]/g, '')                    // no angle brackets → no markup
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, BUSINESS_AD_HEADLINE_MAX);
}

/**
 * Parse + validate a raw kind-30078 event into BusinessAdData.
 *
 * Returns null unless the payload is well-formed AND self-consistent with the
 * signing author — i.e. `businessPubkey === ev.pubkey`. This is the on-wire
 * trust check: an ad can only ever promote the account that signed it, so no
 * one can buy an ad pointing at someone else.
 */
export function parseBusinessAd(ev: {
  pubkey?: string;
  content?: string;
  created_at?: number;
  tags?: string[][];
}): BusinessAdData | null {
  if (!ev || typeof ev.content !== 'string') return null;
  let raw: Partial<BusinessAdData>;
  try {
    raw = JSON.parse(ev.content) as Partial<BusinessAdData>;
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;

  const author = (ev.pubkey || '').trim().toLowerCase();
  const businessPubkey = (raw.businessPubkey || '').trim().toLowerCase();
  // The signer IS the promoted business — a hard trust boundary.
  if (!HEX64_RE.test(author) || businessPubkey !== author) return null;

  // txid: prefer the payload, fall back to the tag the paying re-publish sets.
  let txid = typeof raw.txid === 'string' ? raw.txid.trim().toLowerCase() : '';
  if (!TXID_RE.test(txid) && Array.isArray(ev.tags)) {
    const t = ev.tags.find(x => x[0] === 'txid');
    if (t && typeof t[1] === 'string') txid = t[1].trim().toLowerCase();
  }
  if (!TXID_RE.test(txid)) return null;

  const priceSats = Number(raw.priceSats);
  if (!Number.isFinite(priceSats) || priceSats <= 0) return null;

  const expiresAt = sanitizeExpiresAt(raw.expiresAt, ev.created_at);
  if (expiresAt <= 0) return null;

  const durationDays = Number(raw.durationDays);

  return {
    businessPubkey,
    priceSats: Math.round(priceSats),
    bidUsd: Number.isFinite(Number(raw.bidUsd)) ? Number(raw.bidUsd) : 0,
    durationDays: Number.isFinite(durationDays) && durationDays > 0 ? Math.round(durationDays) : 1,
    expiresAt,
    txid,
    headline: sanitizeHeadline(raw.headline) || undefined,
  };
}

/** Is this parsed ad still within its paid window? */
export function isAdActive(ad: BusinessAdData | null): boolean {
  return !!ad && ad.expiresAt > Math.floor(Date.now() / 1000);
}

/** Convert a USD price to sats. Returns null if the BCH price is unavailable. */
export function adUsdToSats(usd: number, bchPriceUsd: number | null): number | null {
  if (!bchPriceUsd || bchPriceUsd <= 0) return null;
  return Math.ceil((usd / bchPriceUsd) * 1e8);
}

/** Build the event tags + content for a business ad publish. */
export function buildBusinessAdEvent(opts: {
  businessPubkey: string;
  tier: BusinessAdTier;
  priceSats: number;
  bchPriceUsd: number | null;
  txid: string;
  headline?: string;
}): { kind: number; tags: string[][]; content: string } {
  const now = Math.floor(Date.now() / 1000);
  const business = opts.businessPubkey.trim().toLowerCase();
  const headline = sanitizeHeadline(opts.headline);
  const data: BusinessAdData = {
    businessPubkey: business,
    priceSats: Math.round(opts.priceSats),
    bidUsd: opts.tier.priceUsd,
    durationDays: opts.tier.durationDays,
    expiresAt: now + opts.tier.durationSecs,
    txid: opts.txid.trim().toLowerCase(),
    headline: headline || undefined,
  };
  const tags: string[][] = [
    ['d', businessAdDTag(business)],
    ['t', BUSINESS_AD_TAG],
    ['txid', data.txid],
    ['expiration', String(data.expiresAt)], // NIP-40: relays may auto-prune
    ['alt', 'A sponsored business promotion on BCHNostr'],
  ];
  return { kind: BUSINESS_AD_KIND, tags, content: JSON.stringify(data) };
}
